import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react'
import { KeyboardEvent, Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { CurrencyInputFormField, SelectFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Collapse } from '@/components/ui/collapse'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { EmptyState } from '@/components/ui/empty-state'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { SelectOption } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { popupFactory } from '@/factories'
import { formatDollars } from '@/utils/currency'

import {
  ANSWER_PRICE_MAX_LENGTH,
  ANSWER_TITLE_MAX_LENGTH,
  AnswerFormData,
  CUSTOM_PRICE,
  getEmptyAnswer,
  makeAnswerDraftSchema,
  PRICING_TYPE_LABEL_KEYS,
  PRICING_TYPES,
  QuestionFormData,
} from './question-form-modal.constants'
import * as S from './question-form-modal.styled'

const NEW_ANSWER = -1

export interface QuestionAnswersHandle {
  add: VoidFunction
}

interface QuestionAnswersProps {
  ref?: Ref<QuestionAnswersHandle>
  onFormOpenChange?: (isOpen: boolean) => void
}

export const QuestionAnswers = ({ ref, onFormOpenChange }: QuestionAnswersProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { control, formState, getValues, watch } = useFormContext<QuestionFormData>()
  const { fields, append, remove, update } = useFieldArray({ control, name: 'answers' })
  const selectType = watch('selectType')

  // Note: null = form closed, NEW_ANSWER = adding, otherwise the index of the answer being edited
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  // Note: the index whose form is mounted - kept after closing so the form stays while it collapses
  const [formIndex, setFormIndex] = useState<number | null>(null)
  const isFormOpen = editingIndex !== null

  useEffect(() => onFormOpenChange?.(isFormOpen), [isFormOpen, onFormOpenChange])

  const schema = useMemo(() => makeAnswerDraftSchema(t), [t])

  const draft = useForm<AnswerFormData>({
    defaultValues: getEmptyAnswer(),
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const pricingTypeOptions = useMemo<SelectOption<string>[]>(
    () =>
      PRICING_TYPES.filter((value) => selectType !== 'multiple' || value !== CUSTOM_PRICE).map(
        (value) => ({
          label: t(
            `organisation:bookingTypes.question.answers.pricingType.options.${PRICING_TYPE_LABEL_KEYS[value]}`,
          ),
          value,
        }),
      ),
    [selectType, t],
  )

  const openForm = (index: number) => {
    // Note: not `fields[index]` - useFieldArray overwrites each item's `id` with a generated key, which
    // would fail the draft's numeric `id` and silently block saving
    draft.reset(index === NEW_ANSWER ? getEmptyAnswer() : getValues(`answers.${index}`))
    setFormIndex(index)
    setEditingIndex(index)
  }

  useImperativeHandle(ref, () => ({ add: () => openForm(NEW_ANSWER) }))

  const closeForm = () => setEditingIndex(null)

  const saveDraft: SubmitHandler<AnswerFormData> = ({ id, placeholder, pricingType, ...data }) => {
    const answer = {
      id,
      placeholder,
      pricingType,
      pricingValue: data.pricingValue.trim(),
      title: data.title.trim(),
    }
    if (editingIndex === NEW_ANSWER) append(answer)
    else if (editingIndex !== null) update(editingIndex, answer)
    closeForm()
  }

  // Note: Enter inside the draft would otherwise submit the whole question form around it
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || (event.target as HTMLElement).tagName !== 'INPUT') return
    event.preventDefault()
    void draft.handleSubmit(saveDraft)()
  }

  const handleDelete = (index: number) => {
    const answer = fields[index]

    popupFactory.open(
      ConfirmationPopup,
      {
        children: (
          <Trans
            components={[<strong key="title" />]}
            i18nKey="organisation:bookingTypes.question.answers.deleteConfirm.body"
            t={t}
            values={{ title: answer.title }}
          />
        ),
        confirmIcon: TrashIcon,
        confirmText: t('common:actions.delete'),
        onConfirm: () => remove(index),
        title: t('organisation:bookingTypes.question.answers.deleteConfirm.title'),
        variant: 'danger',
      },
      { suspendCurrent: true },
    )
  }

  const getSummary = (answer: AnswerFormData) =>
    [
      t(
        `organisation:bookingTypes.question.answers.pricingType.options.${PRICING_TYPE_LABEL_KEYS[answer.pricingType]}`,
      ),
      answer.pricingType === CUSTOM_PRICE
        ? answer.pricingValue
        : formatDollars(answer.pricingValue, { zeroPrice: true }),
    ]
      .filter(Boolean)
      .join(' · ')

  const renderForm = () => (
    <S.AnswerBody onKeyDown={handleKeyDown}>
      <FormProvider {...draft}>
        <TextInputFormField
          withReservedFooter
          required
          label={t('organisation:bookingTypes.question.answers.title.label')}
          maxLength={ANSWER_TITLE_MAX_LENGTH}
          name="title"
          placeholder={t('organisation:bookingTypes.question.answers.title.placeholder')}
        />
        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <SelectFormField<AnswerFormData, string>
            withReservedFooter
            label={t('organisation:bookingTypes.question.answers.pricingType.label')}
            name="pricingType"
            options={pricingTypeOptions}
            size="medium"
            type="single"
          />
          {draft.watch('pricingType') === CUSTOM_PRICE ? (
            <TextInputFormField
              withReservedFooter
              label={t('organisation:bookingTypes.question.answers.price.label')}
              maxLength={ANSWER_PRICE_MAX_LENGTH}
              name="pricingValue"
              placeholder={t('organisation:bookingTypes.question.answers.price.placeholder')}
            />
          ) : (
            <CurrencyInputFormField
              withReservedFooter
              label={t('organisation:bookingTypes.question.answers.price.label')}
              maxLength={ANSWER_PRICE_MAX_LENGTH}
              name="pricingValue"
              placeholder={t('organisation:bookingTypes.question.answers.price.placeholder')}
            />
          )}
        </Grid>
      </FormProvider>
      <S.AnswerActions>
        <Button size="small" use="secondary" onClick={closeForm}>
          {t('common:actions.cancel')}
        </Button>
        <Button size="small" use="primary" onClick={draft.handleSubmit(saveDraft)}>
          {t('organisation:bookingTypes.question.answers.save')}
          <Icon icon={CheckIcon} />
        </Button>
      </S.AnswerActions>
    </S.AnswerBody>
  )

  return (
    <S.AnswersSection>
      {fields.length === 0 && (
        // Note: the empty state and the new-answer form share one grid cell, so the area is as tall
        // as the taller of the two and nothing jumps when "Add answer" opens the form
        <S.EmptySlot>
          <S.EmptyLayer $stretch $visible={!isFormOpen} inert={isFormOpen}>
            <S.EmptyCard>
              <EmptyState
                description={t('organisation:bookingTypes.question.answers.empty.description')}
                title={t('organisation:bookingTypes.question.answers.empty.title')}
              />
            </S.EmptyCard>
          </S.EmptyLayer>
          <S.EmptyLayer $stretch $visible={isFormOpen} inert={!isFormOpen}>
            <S.AnswerForm $form>{renderForm()}</S.AnswerForm>
          </S.EmptyLayer>
        </S.EmptySlot>
      )}

      {fields.map((field, index) => {
        const error =
          formState.errors.answers?.[index]?.pricingType?.message ??
          formState.errors.answers?.[index]?.pricingValue?.message
        const isEditing = editingIndex === index

        return (
          <S.Answer key={field.id} $form={isEditing}>
            <Collapse in={!isEditing} inert={isEditing}>
              <S.AnswerBody>
                <S.AnswerHeader>
                  <Typography as="span" color="black800" size="text-medium" weight="semibold">
                    {field.title}
                  </Typography>
                  <S.AnswerButtons>
                    <Button
                      onlyBox
                      aria-label={t('organisation:bookingTypes.question.answers.edit')}
                      disabled={isFormOpen}
                      size="small"
                      use="secondary"
                      onClick={() => openForm(index)}
                    >
                      <Icon icon={PencilSimpleIcon} />
                    </Button>
                    <Button
                      onlyBox
                      aria-label={t('organisation:bookingTypes.question.answers.delete')}
                      disabled={isFormOpen}
                      size="small"
                      use="secondary"
                      onClick={() => handleDelete(index)}
                    >
                      <Icon icon={TrashIcon} color="red500" />
                    </Button>
                  </S.AnswerButtons>
                </S.AnswerHeader>
                <Typography as="span" color="black500" size="text-medium">
                  {getSummary(field)}
                </Typography>
                {error && (
                  <Typography as="span" color="red500" size="text-small">
                    {error}
                  </Typography>
                )}
              </S.AnswerBody>
            </Collapse>

            <Collapse in={isEditing} inert={!isEditing}>
              {formIndex === index && renderForm()}
            </Collapse>
          </S.Answer>
        )
      })}

      {fields.length > 0 && (
        <Collapse in={editingIndex === NEW_ANSWER} inert={editingIndex !== NEW_ANSWER}>
          <S.NewAnswerSlot>
            <S.Answer $form>{formIndex === NEW_ANSWER && renderForm()}</S.Answer>
          </S.NewAnswerSlot>
        </Collapse>
      )}
    </S.AnswersSection>
  )
}
